import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ProvisionCanonicalComposition,
  ProvisionCompatibilitySelection,
  ProvisionComponentVersionManifest,
  ProvisionComponentRolloutPlan,
  ProvisionCompositionCandidateRequest,
  ProvisionCompositionCandidateResponse,
  ProvisionCompositionDraft,
  ProvisionCompositionDraftApplyRequest,
  ProvisionCompositionDraftApplyResult,
  ProvisionCompositionDraftPreview,
  ProvisionCompositionDraftUpsertRequest,
  ProvisionCompositionHistoryEntry,
  ProvisionEquipmentRole,
  ProvisionLegacyMatchProposal,
  ProvisionPeripheralManualRecord,
  ProvisionRawFieldDifference,
} from '@reform-lite/contracts';
import { apiClient } from '@/lib/api-client';

export type ProvisionLevel = {
  id: string;
  name: string;
  position: number;
  systemOwned: boolean;
  required: boolean;
};
export type ProvisionNode = {
  id: string;
  levelId: string;
  parentId: string | null;
  code: string;
  name: string;
  version: number;
};
export type ProvisionApplication = {
  id: string;
  code: string;
  intrinsicType: 'pos' | 'sco' | 'attendant';
  status: string;
};
export type ProvisionCompositionCapabilities = {
  schemaVersion: 1;
  managedReadsEnabled: true;
  managedWritesEnabled: boolean;
  manualReadsEnabled: true;
  manualWritesEnabled: true;
};
export type ProvisionEndpoint = {
  id: string;
  nodeId: string;
  code: string;
  displayName: string;
  status: string;
  version: number;
  physicalPath: string[];
  applications: ProvisionApplication[];
};
export type ProvisionGroup = {
  id: string;
  parentId: string | null;
  name: string;
  description: string | null;
  priority: number;
  directMemberCount: number;
  effectiveMemberCount: number;
};
export type CompositionEvidenceState =
  | 'unknown'
  | 'unsupported'
  | 'stale'
  | 'mismatch'
  | 'pending'
  | 'partial'
  | 'in_sync';
export type CompositionDriftDiagnostic = {
  code: string;
  message: string;
  subject: string;
  subjectId: string | null;
  remediation: {
    view: 'composition' | 'hardware' | 'releases' | 'rollouts';
    label: string;
  };
};
export type CompositionComparison = {
  subject: string;
  subjectId: string | null;
  state: CompositionEvidenceState;
  expected: Record<string, string | number | boolean | null> | null;
  observed: Record<string, string | number | boolean | null> | null;
  diagnostic: CompositionDriftDiagnostic | null;
};
export type DriftItem = {
  applicationId: string;
  code: string;
  intrinsicType: string;
  endpointId: string;
  nodeId: string;
  desiredRevision: number | null;
  desiredChecksum: string | null;
  observedCoreVersion: string | null;
  observedRevision: number | null;
  observedChecksum: string | null;
  updateState: string | null;
  lastErrorCode: string | null;
  lastReportedAt: string | null;
  state: string;
  compositionState: CompositionEvidenceState;
  composition: {
    state: CompositionEvidenceState;
    desiredRevisionMatched: boolean;
    report: {
      state: CompositionEvidenceState;
      generation: number | null;
      complete: boolean | null;
      reportedAt: string | null;
    };
    profile: CompositionComparison;
    roles: Array<{
      roleKey: string;
      required: boolean;
      state: CompositionEvidenceState;
      asset: CompositionComparison;
      equipment: CompositionComparison;
      port: CompositionComparison;
      claim: CompositionComparison;
      adapter: CompositionComparison;
      health: CompositionComparison;
    }>;
    components: CompositionComparison[];
    dependencies: CompositionComparison[];
    trust: CompositionComparison[];
    diagnostics: CompositionDriftDiagnostic[];
    counts: Record<CompositionEvidenceState, number>;
  };
};
export type ProvisionDriftReport = {
  items: DriftItem[];
  counts: Record<string, number>;
  compositionCounts: Record<CompositionEvidenceState, number>;
  total: number;
};
export type DesiredStatePreview = {
  values: Record<string, unknown>;
  desiredState: Record<string, unknown>;
  checksum: string;
  sources: Array<{ path: string; assignmentId: string; sourceType: string; sourceId: string }>;
  conflicts: Array<{ path: string; assignmentIds: string[] }>;
};
export type ProvisionCompositionProfileSelection = {
  application: {
    id: string;
    code: string;
    applicationType: 'pos' | 'sco' | 'attendant';
    endpointId: string;
  };
  revision: number;
  selectionMode: 'newest_applicable' | 'pinned';
  selected: {
    profileId: string;
    profileKey: string;
    profileName: string;
    versionId: string;
    version: number;
    applicationType: 'pos' | 'sco' | 'attendant';
    eligible: boolean;
    requiredComponentCapabilities: string[];
    optionalComponentCapabilities: string[];
    roles: ProvisionEquipmentRole[];
  } | null;
  validation: {
    valid: boolean;
    findings: Array<{ severity: string; code: string; message: string }>;
    roles: Array<{
      roleKey: string;
      displayName: string;
      requirement: 'required' | 'optional' | 'conditional';
      requirementReason: string | null;
      requiredNow: boolean;
      scope: 'endpoint' | 'application';
      sharing: 'exclusive' | 'same_host_when_supported';
      minimumCardinality: number;
      maximumCardinality: number;
      assignedCount: number;
      bindingIds: string[];
      conditionEvidence: Array<{ code: string; message: string; matched: boolean }>;
      valid: boolean;
    }>;
  };
};
export type ProvisionEquipmentCatalogueModel = {
  id: string;
  key: string;
  name: string;
  scope: 'reform_global' | 'tenant';
  revisions: Array<{
    id: string;
    revision: number;
    manufacturer: string;
    model: string;
    classKeys: string[];
    supportedRoles: string[];
    configurationSchema: Record<string, unknown>;
    configurationDefaults: Record<string, unknown>;
    certification: string;
    state: string;
    connectionProfiles: Array<{
      id: string;
      key: string;
      connectionType: string;
      compatiblePortKinds: string[];
      sharing: 'exclusive' | 'shared';
      fields: Array<{
        key: string;
        label: string;
        valueType: 'string' | 'integer' | 'boolean' | 'enum' | 'secret_reference';
        required: boolean;
        defaultValue?: unknown;
        allowedValues?: unknown[];
        minimum?: number;
        maximum?: number;
      }>;
      defaults: Record<string, unknown>;
    }>;
  }>;
};
export type ProvisionSoftwareCatalogueComponent = {
  componentId: string;
  key: string;
  displayName: string;
  componentType: string;
  catalogueScope: 'reform_global' | 'tenant';
  description: string | null;
  versions: ProvisionComponentVersionManifest[];
};
export type ProvisionEndpointInventory = {
  schemaVersion: 1;
  endpointId: string;
  ports: Array<{
    portId: string;
    stablePortKey: string;
    kind: string;
    label: string;
    currentAddress: string | null;
    identityStrength: string;
    shareMode: 'exclusive' | 'shared';
    capacity: number | null;
    health: string;
    status: 'present' | 'absent' | 'stale' | 'unknown';
    lastSeenAt: string | null;
  }>;
  applications: Array<{
    applicationInstanceId: string;
    applicationType: string;
    latestGeneration: number | null;
    latestComplete: boolean | null;
    components: Array<{
      observedComponentId: string;
      componentId: string;
      semanticVersion: string;
      componentVersionId: string | null;
      health: 'unknown' | 'healthy' | 'degraded' | 'failed';
      status: 'present' | 'absent' | 'stale' | 'unknown';
      packageEvidence: {
        packageId: string;
        verification: 'verified' | 'rejected' | 'unknown';
        lifecycleState:
          | 'staged'
          | 'installed'
          | 'starting'
          | 'active'
          | 'failed'
          | 'rolling_back'
          | 'rolled_back';
        lastKnownGoodVersionId: string | null;
        errorCode: string | null;
        observedAt: string;
      } | null;
    }>;
  }>;
};

type RawDesiredStatePreview = {
  values: Record<string, unknown>;
  checksum: string;
  sources: Record<
    string,
    Array<{
      id: string;
      sourceType: string;
      sourceId: string;
      winner: boolean;
      reason: string;
    }>
  >;
};

export function normalizeDesiredStatePreview(raw: RawDesiredStatePreview): DesiredStatePreview {
  const sources = Object.entries(raw.sources).flatMap(([path, candidates]) =>
    candidates
      .filter((candidate) => candidate.winner)
      .map((candidate) => ({
        path,
        assignmentId: candidate.id,
        sourceType: candidate.sourceType,
        sourceId: candidate.sourceId,
      })),
  );
  const conflicts = Object.entries(raw.sources).flatMap(([path, candidates]) => {
    const winners = candidates.filter((candidate) => candidate.winner);
    return winners.length <= 1
      ? []
      : [{ path, assignmentIds: winners.map((candidate) => candidate.id) }];
  });
  return {
    values: raw.values,
    desiredState: raw.values,
    checksum: raw.checksum,
    sources,
    conflicts,
  };
}
export type ProvisionTemplateVersion = {
  id: string;
  version: number;
  state: 'draft' | 'published' | 'superseded' | 'archived';
  rowVersion: number;
  config: Record<string, unknown>;
  activationPolicies: Record<string, string>;
  publicationReason: string | null;
  publishedAt: string | null;
};
export type ProvisionTemplate = {
  id: string;
  name: string;
  description: string | null;
  versions: ProvisionTemplateVersion[];
  assignments: Array<{ id: string; targetId: string; followLatest: boolean; priority: number }>;
};
export type ProvisionHardware = {
  id: string;
  classKey: string;
  vendor: string | null;
  model: string | null;
  serialAssetId: string | null;
  connectionType: string;
  connectionParameters: Record<string, unknown>;
  adapterId: string;
  supportedVersion: string | null;
  secretReference: string | null;
  equipmentModelRevisionId: string | null;
  catalogueMatchStatus: 'custom_legacy' | 'proposed' | 'managed' | 'ambiguous' | 'unavailable';
  catalogueMatchMetadata: unknown;
  lifecycle: string;
  observedHealth: string;
  diagnosticCode: string | null;
  endpointId: string | null;
  endpoint: { code: string; displayName: string } | null;
  version: number;
  bindings: Array<{
    id: string;
    applicationInstanceId: string | null;
    logicalRole: string;
    required: boolean;
    config: Record<string, unknown>;
    connectionProfileId: string | null;
    endpointPortId: string | null;
    adapterComponentVersionId: string | null;
    catalogueMatchStatus: 'custom_legacy' | 'proposed' | 'managed' | 'ambiguous' | 'unavailable';
    catalogueMatchMetadata: unknown;
  }>;
};

export type ProvisionManualUpdateInput = {
  expectedVersion: number;
  classKey: string;
  vendor: string | null;
  model: string | null;
  serialAssetId: string | null;
  connectionType: string;
  connectionParameters: Record<string, unknown>;
  adapterId: string;
  supportedVersion: string | null;
  secretReference: string | null;
  bindings: Array<{
    bindingId: string;
    applicationInstanceId: string | null;
    logicalRole: string;
    required: boolean;
    config: Record<string, unknown>;
  }>;
};

export type ProvisionManualUpdatePreview = {
  schemaVersion: 1;
  deviceId: string;
  expectedVersion: number;
  changed: boolean;
  blocked: boolean;
  activeOperations: number;
  differences: ProvisionRawFieldDifference[];
  previewChecksum: string;
  current: ProvisionPeripheralManualRecord;
};

export type ProvisionManualAuditEntry = {
  auditId: string;
  action: string;
  entityType: string;
  entityId: string | null;
  actor: { id: string | null; displayName: string | null; email: string | null };
  before: unknown;
  after: unknown;
  context: unknown;
  occurredAt: string;
};

export type ProvisionEquipmentLinkPreview = {
  operation: 'link' | 'unlink';
  deviceId: string;
  expectedVersion: number;
  currentEquipmentModelRevisionId: string | null;
  proposedEquipmentModelRevisionId: string | null;
  proposedMatchStatus: 'managed' | 'custom_legacy';
  exactRawMatch: boolean;
  rawFieldsChanged: false;
  differences: ProvisionRawFieldDifference[];
  catalogue?: Record<string, unknown>;
  previewChecksum: string;
};

export type ProvisionBindingLinkReferences = {
  connectionProfileId: string | null;
  endpointPortId: string | null;
  adapterComponentVersionId: string | null;
};

export type ProvisionBindingLinkPreview = {
  schemaVersion: 1;
  deviceId: string;
  bindingId: string;
  expectedVersion: number;
  references: ProvisionBindingLinkReferences;
  equipmentModelRevisionId?: string;
  differences: ProvisionRawFieldDifference[];
  matchStatus: 'managed' | 'custom_legacy';
  connectionSharing: 'exclusive' | 'shared' | null;
  catalogueSnapshots: Record<string, unknown> | null;
  previewChecksum: string;
};
export type ImpactPreview = {
  previewChecksum: string;
  confirmationPhrase: string | null;
  counts: Record<string, number>;
};
export type ProvisionTarget = {
  id: string;
  label: string;
  priority: number;
  applicationType: string | null;
  contentAssignments: Array<{
    id: string;
    kind: 'assortment' | 'hotkey_layout' | 'country_pack';
    contentDefinitionId: string | null;
    countryPackId: string | null;
    followLatest: boolean;
  }>;
};
export type ProvisionContentDefinition = {
  id: string;
  kind: 'assortment' | 'hotkey_layout';
  name: string;
  isDefault: boolean;
  versions: Array<{ id: string; version: number; state: string; checksum: string }>;
};
export type ProvisionArtifact = {
  id: string;
  kind: 'core' | 'ui';
  applicationType: string;
  semanticVersion: string;
  platform: string;
  architecture: string;
  sha256: string;
  status: string;
  coreVersionRange: string | null;
  uiPortVersionRange: string | null;
  approvals: Array<{
    id: string;
    status: 'approved' | 'revoked';
    reason: string;
    decidedAt: string;
  }>;
};
export type ProvisionRollout = {
  id: string;
  name: string;
  coreArtifactId: string | null;
  uiArtifactId: string | null;
  kind: 'legacy_artifact_pair' | 'component_plan';
  componentPlan: Record<string, unknown> | null;
  planChecksum: string | null;
  state: string;
  reason: string;
  scheduledAt: string | null;
  rollbackOfId: string | null;
  stages: Array<{
    id: string;
    position: number;
    name: string;
    pilot: boolean;
    state: string;
    targets: Array<{
      id: string;
      applicationInstanceId: string;
      state: string;
      lastErrorCode: string | null;
      componentPlan: ProvisionComponentRolloutPlan | null;
      componentProgress: {
        planChecksum: string;
        completedStepIds: string[];
        stepResults: Array<{ stepId: string; state: 'succeeded' | 'failed' | 'skipped' }>;
      } | null;
    }>;
  }>;
};
export type RolloutPreview = {
  previewChecksum: string;
  confirmationPhrase: string | null;
  applicationIds: string[];
  counts: Record<string, number>;
};
export type ComponentRolloutPreview = RolloutPreview & {
  plans: ProvisionComponentRolloutPlan[];
  incompatibleApplications: Array<{ applicationInstanceId: string; reason: string }>;
};

const ROOT = ['provision'] as const;
const refresh = (qc: ReturnType<typeof useQueryClient>) => qc.invalidateQueries({ queryKey: ROOT });

export function useProvisionTopology() {
  return useQuery({
    queryKey: [...ROOT, 'topology'],
    queryFn: async () =>
      (
        await apiClient.get<{ levels: ProvisionLevel[]; nodes: ProvisionNode[] }>(
          '/provision/topology',
        )
      ).data,
  });
}

export type ProvisionAccessMode = 'admin' | 'technician';

const provisionBase = (mode: ProvisionAccessMode) =>
  mode === 'technician' ? '/provision/technician' : '/provision';

export function useProvisionFleet(mode: ProvisionAccessMode = 'admin') {
  return useQuery({
    queryKey: [...ROOT, mode, 'fleet'],
    queryFn: async () =>
      (await apiClient.get<ProvisionEndpoint[]>(`${provisionBase(mode)}/fleet`)).data,
    refetchInterval: 30_000,
  });
}

export function useProvisionGroups() {
  return useQuery({
    queryKey: [...ROOT, 'groups'],
    queryFn: async () => (await apiClient.get<ProvisionGroup[]>('/provision/groups')).data,
  });
}

export function useProvisionDrift(
  filters: {
    state?: string;
    compositionState?: CompositionEvidenceState;
    diagnosticCode?: string;
  } = {},
  mode: ProvisionAccessMode = 'admin',
) {
  return useQuery({
    queryKey: [...ROOT, mode, 'drift', filters],
    queryFn: async () =>
      (
        await apiClient.get<ProvisionDriftReport>(`${provisionBase(mode)}/drift`, {
          params: filters,
        })
      ).data,
    refetchInterval: 30_000,
  });
}

export function useProvisionTemplates() {
  return useQuery({
    queryKey: [...ROOT, 'templates'],
    queryFn: async () => (await apiClient.get<ProvisionTemplate[]>('/provision/templates')).data,
  });
}

export function useProvisionHardware() {
  return useQuery({
    queryKey: [...ROOT, 'hardware'],
    queryFn: async () => (await apiClient.get<ProvisionHardware[]>('/provision/hardware')).data,
    refetchInterval: 30_000,
  });
}

export function useProvisionTargets() {
  return useQuery({
    queryKey: [...ROOT, 'targets'],
    queryFn: async () => (await apiClient.get<ProvisionTarget[]>('/provision/targets')).data,
  });
}

export function useProvisionContentDefinitions() {
  return useQuery({
    queryKey: [...ROOT, 'content-definitions'],
    queryFn: async () =>
      (await apiClient.get<ProvisionContentDefinition[]>('/provision/content-definitions')).data,
  });
}

export function useProvisionArtifacts() {
  return useQuery({
    queryKey: [...ROOT, 'artifacts'],
    queryFn: async () => (await apiClient.get<ProvisionArtifact[]>('/provision/artifacts')).data,
  });
}

export function useProvisionRollouts() {
  return useQuery({
    queryKey: [...ROOT, 'rollouts'],
    queryFn: async () => (await apiClient.get<ProvisionRollout[]>('/provision/rollouts')).data,
    refetchInterval: 15_000,
  });
}

export function useDesiredStatePreview(
  applicationId: string | null,
  mode: ProvisionAccessMode = 'admin',
) {
  return useQuery({
    queryKey: [...ROOT, mode, 'desired-state', applicationId],
    enabled: applicationId !== null,
    queryFn: async () =>
      normalizeDesiredStatePreview(
        (
          await apiClient.get<RawDesiredStatePreview>(
            `${provisionBase(mode)}/applications/${applicationId ?? ''}/desired-state/preview`,
          )
        ).data,
      ),
  });
}

export function useProvisionCompositionProfile(
  applicationId: string | null,
  mode: ProvisionAccessMode = 'admin',
) {
  return useQuery({
    queryKey: [...ROOT, mode, 'composition-profile', applicationId],
    enabled: applicationId !== null,
    queryFn: async () =>
      (
        await apiClient.get<ProvisionCompositionProfileSelection>(
          `${provisionBase(mode)}/applications/${applicationId ?? ''}/composition-profile`,
        )
      ).data,
  });
}

export function useProvisionCompositionCapabilities() {
  return useQuery({
    queryKey: [...ROOT, 'composition-capabilities'],
    queryFn: async () =>
      (await apiClient.get<ProvisionCompositionCapabilities>('/provision/composition-capabilities'))
        .data,
    staleTime: 30_000,
  });
}

export function useProvisionCompositionDraft(
  applicationId: string | null,
  mode: ProvisionAccessMode = 'admin',
) {
  return useQuery({
    queryKey: [...ROOT, mode, 'composition-draft', applicationId],
    enabled: applicationId !== null,
    queryFn: async () =>
      (
        await apiClient.get<ProvisionCompositionDraft | null>(
          `${provisionBase(mode)}/applications/${applicationId ?? ''}/composition-draft`,
        )
      ).data,
  });
}

export function useProvisionCompositionHistory(
  applicationId: string | null,
  mode: ProvisionAccessMode = 'admin',
) {
  return useQuery({
    queryKey: [...ROOT, mode, 'composition-history', applicationId],
    enabled: applicationId !== null,
    queryFn: async () =>
      (
        await apiClient.get<ProvisionCompositionHistoryEntry[]>(
          `${provisionBase(mode)}/applications/${applicationId ?? ''}/composition-history`,
        )
      ).data,
  });
}

export function useProvisionCompositionCandidates(
  applicationId: string | null,
  request: ProvisionCompositionCandidateRequest | null,
  mode: ProvisionAccessMode = 'admin',
) {
  return useQuery({
    queryKey: [...ROOT, mode, 'composition-candidates', applicationId, request],
    enabled: applicationId !== null && request !== null,
    queryFn: async () =>
      (
        await apiClient.post<ProvisionCompositionCandidateResponse>(
          `${provisionBase(mode)}/applications/${applicationId ?? ''}/composition-candidates`,
          request,
        )
      ).data,
  });
}

export function useProvisionEquipmentCatalogue(
  applicationType: ProvisionApplication['intrinsicType'] | null,
  options?: { enabled?: boolean; mode?: ProvisionAccessMode },
) {
  const mode = options?.mode ?? 'admin';
  return useQuery({
    queryKey: [...ROOT, mode, 'equipment-models', applicationType],
    enabled: options?.enabled ?? applicationType !== null,
    queryFn: async () =>
      (
        await apiClient.get<ProvisionEquipmentCatalogueModel[]>(
          `${provisionBase(mode)}/equipment-models`,
          {
            params: {
              ...(mode === 'admin' ? { state: 'published' } : {}),
              ...(applicationType === null ? {} : { applicationType }),
            },
          },
        )
      ).data,
  });
}

export function useProvisionSoftwareCatalogue(
  applicationType: ProvisionApplication['intrinsicType'] | null,
  options?: { enabled?: boolean; mode?: ProvisionAccessMode },
) {
  const mode = options?.mode ?? 'admin';
  return useQuery({
    queryKey: [...ROOT, mode, 'software-components', applicationType],
    enabled: options?.enabled ?? applicationType !== null,
    queryFn: async () =>
      (
        await apiClient.get<ProvisionSoftwareCatalogueComponent[]>(
          `${provisionBase(mode)}/software-components`,
          {
            params: {
              ...(mode === 'admin' ? { state: 'published' } : {}),
              ...(applicationType === null ? {} : { applicationType }),
            },
          },
        )
      ).data,
  });
}

export function useProvisionEndpointInventory(
  endpointId: string | null,
  mode: ProvisionAccessMode = 'admin',
) {
  return useQuery({
    queryKey: [...ROOT, mode, 'endpoint-inventory', endpointId],
    enabled: endpointId !== null,
    queryFn: async () =>
      (
        await apiClient.get<ProvisionEndpointInventory>(
          `${provisionBase(mode)}/endpoints/${endpointId ?? ''}/inventory`,
        )
      ).data,
    refetchInterval: 30_000,
  });
}

export function useSaveProvisionCompositionDraft(mode: ProvisionAccessMode = 'admin') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      applicationId: string;
      draft: ProvisionCompositionDraftUpsertRequest;
    }) =>
      (
        await apiClient.put<ProvisionCompositionDraft>(
          `${provisionBase(mode)}/applications/${input.applicationId}/composition-draft`,
          input.draft,
        )
      ).data,
    onSuccess: (draft, input) => {
      qc.setQueryData([...ROOT, mode, 'composition-draft', input.applicationId], draft);
      return refresh(qc);
    },
  });
}

export function usePreviewProvisionCompositionDraft(mode: ProvisionAccessMode = 'admin') {
  return useMutation({
    mutationFn: async (input: { applicationId: string; expectedDraftVersion: number }) =>
      (
        await apiClient.post<ProvisionCompositionDraftPreview>(
          `${provisionBase(mode)}/applications/${input.applicationId}/composition-draft/preview`,
          { schemaVersion: 1, expectedDraftVersion: input.expectedDraftVersion },
        )
      ).data,
  });
}

export function useApplyProvisionCompositionDraft(mode: ProvisionAccessMode = 'admin') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      applicationId: string;
      request: ProvisionCompositionDraftApplyRequest;
    }) =>
      (
        await apiClient.post<ProvisionCompositionDraftApplyResult>(
          `${provisionBase(mode)}/applications/${input.applicationId}/composition-draft/apply`,
          input.request,
        )
      ).data,
    onSuccess: () => refresh(qc),
  });
}

export function useProvisionManualHardware(deviceId: string | null) {
  return useQuery({
    queryKey: [...ROOT, 'manual-hardware', deviceId],
    enabled: deviceId !== null,
    queryFn: async () =>
      (
        await apiClient.get<ProvisionPeripheralManualRecord>(
          `/provision/hardware/${deviceId ?? ''}/manual`,
        )
      ).data,
  });
}

export function useProvisionManualHardwareHistory(deviceId: string | null) {
  return useQuery({
    queryKey: [...ROOT, 'manual-hardware-history', deviceId],
    enabled: deviceId !== null,
    queryFn: async () =>
      (
        await apiClient.get<ProvisionManualAuditEntry[]>(
          `/provision/hardware/${deviceId ?? ''}/manual-history`,
        )
      ).data,
  });
}

export function useProvisionLegacyMatchProposals(deviceId: string | null) {
  return useQuery({
    queryKey: [...ROOT, 'legacy-match-proposals', deviceId],
    enabled: deviceId !== null,
    queryFn: async () =>
      (
        await apiClient.get<{
          device: ProvisionLegacyMatchProposal | null;
          bindings: ProvisionLegacyMatchProposal[];
        }>(`/provision/hardware/${deviceId ?? ''}/legacy-match-proposals`)
      ).data,
  });
}

export function usePreviewProvisionManualHardware() {
  return useMutation({
    mutationFn: async (input: { deviceId: string; values: ProvisionManualUpdateInput }) =>
      (
        await apiClient.post<ProvisionManualUpdatePreview>(
          `/provision/hardware/${input.deviceId}/manual-update-preview`,
          input.values,
        )
      ).data,
  });
}

export function useApplyProvisionManualHardware() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      deviceId: string;
      values: ProvisionManualUpdateInput;
      previewChecksum: string;
      reason: string;
    }) =>
      (
        await apiClient.put<{
          changed: boolean;
          desiredStateRevisionCreated: false;
          record: ProvisionPeripheralManualRecord;
        }>(`/provision/hardware/${input.deviceId}/manual`, {
          ...input.values,
          previewChecksum: input.previewChecksum,
          reason: input.reason,
        })
      ).data,
    onSuccess: () => refresh(qc),
  });
}

export function usePreviewProvisionEquipmentLink() {
  return useMutation({
    mutationFn: async (input: { deviceId: string; equipmentModelRevisionId: string | null }) =>
      (
        await apiClient.get<ProvisionEquipmentLinkPreview>(
          `/provision/hardware/${input.deviceId}/equipment-model-link-preview`,
          {
            params:
              input.equipmentModelRevisionId === null
                ? {}
                : { equipmentModelRevisionId: input.equipmentModelRevisionId },
          },
        )
      ).data,
  });
}

export function useApplyProvisionEquipmentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      deviceId: string;
      equipmentModelRevisionId: string | null;
      expectedVersion: number;
      previewChecksum: string;
      acknowledgeDifferences: boolean;
      reason: string;
    }) =>
      (
        await apiClient.post(`/provision/hardware/${input.deviceId}/equipment-model-link`, {
          equipmentModelRevisionId: input.equipmentModelRevisionId,
          expectedVersion: input.expectedVersion,
          previewChecksum: input.previewChecksum,
          acknowledgeDifferences: input.acknowledgeDifferences,
          reason: input.reason,
        })
      ).data,
    onSuccess: () => refresh(qc),
  });
}

export function usePreviewProvisionBindingLink() {
  return useMutation({
    mutationFn: async (input: {
      deviceId: string;
      bindingId: string;
      references: ProvisionBindingLinkReferences;
    }) =>
      (
        await apiClient.get<ProvisionBindingLinkPreview>(
          `/provision/hardware/${input.deviceId}/bindings/${input.bindingId}/catalogue-link-preview`,
          { params: input.references },
        )
      ).data,
  });
}

export function useApplyProvisionBindingLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      deviceId: string;
      bindingId: string;
      references: ProvisionBindingLinkReferences;
      expectedVersion: number;
      previewChecksum: string;
      acknowledgeDifferences: boolean;
      reason: string;
    }) =>
      (
        await apiClient.post(
          `/provision/hardware/${input.deviceId}/bindings/${input.bindingId}/catalogue-link`,
          {
            ...input.references,
            expectedVersion: input.expectedVersion,
            previewChecksum: input.previewChecksum,
            acknowledgeDifferences: input.acknowledgeDifferences,
            reason: input.reason,
          },
        )
      ).data,
    onSuccess: () => refresh(qc),
  });
}

export type {
  ProvisionCanonicalComposition,
  ProvisionCompatibilitySelection,
  ProvisionCompositionCandidateRequest,
  ProvisionCompositionCandidateResponse,
  ProvisionCompositionDraft,
  ProvisionCompositionDraftApplyRequest,
  ProvisionCompositionDraftApplyResult,
  ProvisionCompositionDraftPreview,
  ProvisionCompositionHistoryEntry,
  ProvisionLegacyMatchProposal,
  ProvisionPeripheralManualRecord,
  ProvisionRawFieldDifference,
};

export function useCreateHierarchyLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; position: number }) =>
      (await apiClient.post('/provision/hierarchy-levels', body)).data,
    onSuccess: () => refresh(qc),
  });
}

export function useCreatePhysicalNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { levelId: string; parentId?: string; code: string; name: string }) =>
      (await apiClient.post('/provision/nodes', body)).data,
    onSuccess: () => refresh(qc),
  });
}

export function useCreateProvisionGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      description?: string;
      parentId?: string;
      priority: number;
    }) => (await apiClient.post('/provision/groups', body)).data,
    onSuccess: () => refresh(qc),
  });
}

export function useProvisionGroupMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { groupId: string; endpointId: string; add: boolean }) => {
      const url = `/provision/groups/${input.groupId}/members/${input.endpointId}`;
      return input.add ? (await apiClient.post(url, {})).data : (await apiClient.delete(url)).data;
    },
    onSuccess: () => refresh(qc),
  });
}

export function useCreateProvisionTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      description?: string;
      config: Record<string, unknown>;
      activationPolicies: Record<string, string>;
    }) => (await apiClient.post('/provision/templates', body)).data,
    onSuccess: () => refresh(qc),
  });
}

export function useCreateTemplateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) =>
      (await apiClient.post(`/provision/templates/${templateId}/drafts`, {})).data,
    onSuccess: () => refresh(qc),
  });
}

export function useTemplatePublicationPreview() {
  return useMutation({
    mutationFn: async (versionId: string) =>
      (
        await apiClient.get<ImpactPreview>(
          `/provision/template-versions/${versionId}/publication-preview`,
        )
      ).data,
  });
}

export function usePublishTemplateVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      versionId: string;
      expectedRowVersion: number;
      previewChecksum: string;
      confirmationPhrase?: string;
      reason: string;
    }) => {
      const { versionId, ...body } = input;
      return (await apiClient.post(`/provision/template-versions/${versionId}/publish`, body)).data;
    },
    onSuccess: () => refresh(qc),
  });
}

export function useCreateProvisionHardware() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      classKey: string;
      vendor?: string;
      model?: string;
      serialAssetId?: string;
      connectionType: string;
      connectionParameters: Record<string, unknown>;
      adapterId: string;
      secretReference?: string;
    }) => (await apiClient.post('/provision/hardware', body)).data,
    onSuccess: () => refresh(qc),
  });
}

export function useAssignProvisionHardware() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      deviceId: string;
      endpointId: string;
      expectedVersion: number;
      logicalRole: string;
    }) =>
      (
        await apiClient.post(`/provision/hardware/${input.deviceId}/assign`, {
          endpointId: input.endpointId,
          expectedVersion: input.expectedVersion,
          bindings: [{ logicalRole: input.logicalRole, required: true, config: {} }],
        })
      ).data,
    onSuccess: () => refresh(qc),
  });
}

export function useMutateProvisionHardwareLifecycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      deviceId: string;
      action: 'unassign' | 'retire';
      expectedVersion: number;
      reason: string;
    }) =>
      (
        await apiClient.post(`/provision/hardware/${input.deviceId}/${input.action}`, {
          expectedVersion: input.expectedVersion,
          reason: input.reason,
        })
      ).data,
    onSuccess: () => refresh(qc),
  });
}

export function useCreateProvisionContentDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      kind: 'assortment' | 'hotkey_layout';
      name: string;
      payload: Record<string, unknown>;
    }) => (await apiClient.post('/provision/content-definitions', body)).data,
    onSuccess: () => refresh(qc),
  });
}

export function useCreateProvisionContentAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      kind: 'assortment' | 'hotkey_layout';
      targetId: string;
      contentDefinitionId: string;
      followLatest: boolean;
      pinnedVersionId?: string;
      priority: number;
    }) => (await apiClient.post('/provision/content-assignments', body)).data,
    onSuccess: () => refresh(qc),
  });
}

export function useDecideProvisionArtifact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      artifactId: string;
      action: 'approve' | 'revoke';
      reason: string;
    }) =>
      (
        await apiClient.post(`/provision/artifacts/${input.artifactId}/${input.action}`, {
          reason: input.reason,
        })
      ).data,
    onSuccess: () => refresh(qc),
  });
}

export function usePreviewProvisionRollout() {
  return useMutation({
    mutationFn: async (body: {
      coreArtifactId: string;
      uiArtifactId: string;
      targetIds: string[];
    }) => (await apiClient.post<RolloutPreview>('/provision/rollouts/preview', body)).data,
  });
}

export function useCreateProvisionRollout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      coreArtifactId: string;
      uiArtifactId: string;
      targetIds: string[];
      pilotApplicationIds: string[];
      scheduledAt?: string | null;
      timezone: string;
      downloadConcurrencyStore: number;
      installConcurrencyStore: number;
      bandwidthLimitKbps?: number | null;
      observationMinutes: number;
      reason: string;
      previewChecksum: string;
      confirmationPhrase?: string;
    }) => (await apiClient.post('/provision/rollouts', body)).data,
    onSuccess: () => refresh(qc),
  });
}

export function usePreviewProvisionComponentRollout() {
  return useMutation({
    mutationFn: async (body: { targetIds: string[]; componentVersionIds: string[] }) =>
      (await apiClient.post<ComponentRolloutPreview>('/provision/rollouts/component-preview', body))
        .data,
  });
}

export function useCreateProvisionComponentRollout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      targetIds: string[];
      componentVersionIds: string[];
      pilotApplicationIds: string[];
      scheduledAt?: string | null;
      timezone: string;
      downloadConcurrencyStore: number;
      installConcurrencyStore: number;
      bandwidthLimitKbps?: number | null;
      observationMinutes: number;
      reason: string;
      previewChecksum: string;
      confirmationPhrase?: string;
    }) => (await apiClient.post('/provision/rollouts/components', body)).data,
    onSuccess: () => refresh(qc),
  });
}

export function useConfirmProvisionComponentRolloutStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { targetId: string; stepId: string; reason: string }) =>
      (
        await apiClient.post(
          `/provision/component-rollout-targets/${input.targetId}/confirm-step`,
          { stepId: input.stepId, reason: input.reason },
        )
      ).data,
    onSuccess: () => refresh(qc),
  });
}

export function useMutateProvisionRollout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      rolloutId: string;
      action: 'pause' | 'resume' | 'cancel' | 'approve-next-stage';
      reason: string;
    }) =>
      (
        await apiClient.post(`/provision/rollouts/${input.rolloutId}/${input.action}`, {
          reason: input.reason,
        })
      ).data,
    onSuccess: () => refresh(qc),
  });
}

export function useCreateProvisionRollback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      sourceRolloutId: string;
      coreArtifactId: string;
      uiArtifactId: string;
      pilotApplicationIds: string[];
      reason: string;
      previewChecksum: string;
      confirmationPhrase?: string;
    }) => (await apiClient.post('/provision/rollouts/rollback', body)).data,
    onSuccess: () => refresh(qc),
  });
}

export function usePreviewProvisionRollback() {
  return useMutation({
    mutationFn: async (body: {
      sourceRolloutId: string;
      coreArtifactId: string;
      uiArtifactId: string;
    }) => (await apiClient.post<RolloutPreview>('/provision/rollouts/rollback-preview', body)).data,
  });
}
